import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { useSession } from '../session';
import { api, type ApiError } from '../api';
import { BrandMark } from '../components/Brand';
import { Button } from '../components/ui/button';
import type { Judge } from '../../shared/types';

export default function Auth() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { establishSession } = useSession();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get('token');
    if (!token) {
      setError('This sign-in link is missing its token.');
      return;
    }
    api<{ role: 'judge'; token: string; judge: Judge }>('verify-link', { body: { token }, auth: false })
      .then((data) => {
        establishSession(data);
        navigate('/', { replace: true });
      })
      .catch((e) => setError((e as ApiError).message || 'This sign-in link is invalid or has expired.'));
  }, [params, establishSession, navigate]);

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-background px-4 text-center">
      <BrandMark className="size-16 ring-1 ring-border" />
      {error ? (
        <>
          <AlertCircle className="mt-6 size-8 text-destructive" />
          <p className="mt-3 max-w-xs text-muted-foreground">{error}</p>
          <Button asChild variant="secondary" className="mt-5">
            <Link to="/login">Back to sign in</Link>
          </Button>
        </>
      ) : (
        <>
          <Loader2 className="mt-6 size-8 animate-spin text-primary" />
          <p className="mt-3 text-muted-foreground">Signing you in…</p>
        </>
      )}
    </div>
  );
}
